import Header from "../../../shared/app-shell/Header";
import Sidebar from "../../../shared/app-shell/Sidebar";
import { themeAtom } from "../../../shared/atoms";
import { log, logV } from "../../../shared/services/debug-logger";
import TfHullEvaluationActionModal from "../components/TfHullEvaluationActionModal";
import TfHullEvaluationsFilters from "../components/TfHullEvaluationsFilters";
import TfHullEvaluationsTable from "../components/TfHullEvaluationsTable";
import {
  EMPTY_TF_HULL_FILTERS,
  TfHullEvaluationAction,
  TfHullEvaluationFilters,
} from "../models/tf-hull-evaluations.models";
import { useTfHullEvaluations } from "../state/useTfHullEvaluations";
import { ArrowPathIcon, ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

const emptyFilters = (): TfHullEvaluationFilters => ({
  ...EMPTY_TF_HULL_FILTERS,
  contractTypes: [],
  statuses: [],
  hasEmail: [],
});

const isWithinRange = (value: number | null, from: string, to: string): boolean => {
  if (!from && !to) return true;
  if (value === null) return false;
  return (!from || value >= Number(from)) && (!to || value <= Number(to));
};

// Reproduces the Power BI table, slicers, and bookmark shells while Snowflake remains the live-data source.
const TfHullEvaluationsPage = () => {
  const theme = useAtomValue(themeAtom);
  const { response, error, loading, mockEnabled, refresh } = useTfHullEvaluations(true);
  const [filters, setFilters] = useState<TfHullEvaluationFilters>(emptyFilters);
  const [action, setAction] = useState<TfHullEvaluationAction>();
  const allVessels = useMemo(() => response?.vessels ?? [], [response?.vessels]);

  const vessels = useMemo(
    () =>
      allVessels
        .filter(
          (vessel) =>
            (!filters.imo || vessel.imo === filters.imo) &&
            (!filters.vesselName || vessel.vesselName === filters.vesselName) &&
            (!filters.statuses.length || filters.statuses.includes(vessel.status)) &&
            (!filters.contractTypes.length || filters.contractTypes.includes(vessel.contractType)) &&
            (!filters.techFormStatus || vessel.techFormStatus === filters.techFormStatus) &&
            (!filters.hasEmail.length || filters.hasEmail.includes(String(vessel.hasEmailInImos))) &&
            (!filters.vesselType || vessel.vesselType === filters.vesselType) &&
            isWithinRange(vessel.deliveredSinceDays, filters.deliveredFrom, filters.deliveredTo) &&
            isWithinRange(vessel.enteredSinceDays, filters.enteredFrom, filters.enteredTo) &&
            (!filters.manualReminder || (filters.manualReminder === "Yes") === Boolean(vessel.latestManualReminder)) &&
            (!filters.hullAssessment || (filters.hullAssessment === "Yes") === Boolean(vessel.hullAssessmentCheck)),
        )
        .sort(
          (left, right) =>
            (left.deliveredSinceDays ?? Number.POSITIVE_INFINITY) -
            (right.deliveredSinceDays ?? Number.POSITIVE_INFINITY),
        ),
    [allVessels, filters],
  );

  const filteredContractCount = vessels.filter((vessel) => vessel.contract).length;
  logV("TfHullEvaluationsPage - BUILD", { count: vessels.length, loading });

  const openAction = (nextAction: TfHullEvaluationAction) => {
    log("TfHullEvaluationsPage - openAction", { action: nextAction, count: vessels.length });
    setAction(nextAction);
  };

  const closeAction = () => {
    log("TfHullEvaluationsPage - closeAction");
    setAction(undefined);
  };

  return (
    <div className="flex h-screen">
      <Sidebar />
      <div
        className={twMerge(
          "flex min-w-0 flex-1 flex-col overflow-hidden",
          theme.home.tailwind.backgound,
          theme.home.tailwind.textDefault,
        )}
      >
        <Header />
        <main className="flex-1 overflow-auto px-4 pb-6 pt-3 sm:px-6" data-test="tf-hull-evaluations-page">
          <div className="mx-auto max-w-[1900px]">
            <div className="mb-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <ClipboardDocumentCheckIcon className="h-8 w-8 text-green" />
                <div>
                  <h1 className="text-xl font-semibold">Tech Forms and Hull Evaluation</h1>
                  <p className="text-xs text-gray-500">
                    Tech Follower · {mockEnabled ? "modeled mock data" : "live Snowflake data"}
                  </p>
                </div>
              </div>
              <div className="rounded-lg bg-primary-10 px-4 py-2 text-right">
                <div className="text-xl font-semibold text-green">{filteredContractCount}</div>
                <div className="text-[10px] uppercase">Contracts</div>
              </div>
            </div>
            <div className="grid items-start gap-4 lg:grid-cols-[245px_minmax(0,1fr)]">
              <TfHullEvaluationsFilters
                filters={filters}
                onChange={setFilters}
                onClear={() => setFilters(emptyFilters())}
                onOpenAction={openAction}
                vessels={allVessels}
              />
              <section className="min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <div className="flex items-center justify-between border-b px-4 py-3 dark:border-gray-700">
                  <div>
                    <h2 className="text-sm font-semibold">Tech forms and hull evaluation follower</h2>
                    {error && <p className="text-xs text-red-600">{error}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => void refresh()}
                    disabled={loading}
                    className="flex items-center gap-2 rounded border px-3 py-2 text-xs"
                    data-test="tf-hull-refresh"
                  >
                    <ArrowPathIcon className={twMerge("h-4 w-4", loading && "animate-spin")} />
                    Refresh
                  </button>
                </div>
                <TfHullEvaluationsTable loading={loading} vessels={vessels} />
              </section>
            </div>
          </div>
        </main>
      </div>
      {action && <TfHullEvaluationActionModal action={action} onClose={closeAction} vessels={vessels} />}
    </div>
  );
};

export default TfHullEvaluationsPage;
